import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { AuthService } from '@cnt-workspace/data-access';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonLabel,
  IonMenuButton,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';

/**
 * Admin shell: toolbar + routed content. The side menu (`admin-header`) lives
 * at the app root, not here — this component is destroyed and recreated on
 * every top-level navigation (each admin route renders it directly), and
 * Ionic's menu controller doesn't reliably re-register a menu that's
 * repeatedly torn down and rebuilt. Session comes from cnt-landing's
 * AuthService.
 */
@Component({
  selector: 'admin-layout',
  standalone: true,
  imports: [
    AsyncPipe,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonLabel,
    IonMenuButton,
    IonTitle,
    IonToolbar,
    RouterOutlet,
  ],
  template: `
    <div class="page admin-grid-bg">
      <ion-header class="ion-no-border">
        <ion-toolbar>
          <ion-buttons slot="start">
            <ion-menu-button></ion-menu-button>
          </ion-buttons>
          <ion-title>CurbNTurf Admin</ion-title>
          <ion-buttons slot="end">
            @if (auth.currentUser$ | async; as user) {
              <span class="admin-user">
                <span class="avatar" aria-hidden="true">{{ initials(user.firstName, user.lastName) }}</span>
                <ion-label class="name">{{ user.firstName }} {{ user.lastName }}</ion-label>
              </span>
            }
            <ion-button fill="outline" size="small" (click)="logout()">
              <ion-label>Log Out</ion-label>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content fullscreen>
        <div class="admin-container">
          <router-outlet></router-outlet>
        </div>
      </ion-content>
    </div>
  `,
  styles: [
    `
      /* Full-height column for the header + scrolling content. */
      .page {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
      /* A floating white bar over the grid, like cnt-landing's navbar. */
      ion-header {
        padding: 12px 12px 0;
        background: transparent;
      }
      ion-toolbar {
        --background: #fff;
        --border-width: 0;
        --min-height: 60px;
        --padding-start: 10px;
        --padding-end: 12px;
        border: 1px solid var(--cnt-line);
        border-radius: 1rem;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
      }
      ion-content {
        --background: transparent;
      }
      ion-title {
        font-size: 1.0625rem;
        font-weight: 700;
        letter-spacing: -0.01em;
      }
      .admin-user {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        margin-right: 12px;
      }
      .avatar {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        border-radius: 9999px;
        background: var(--cnt-green);
        color: #fff;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.04em;
      }
      .name {
        font-size: 0.875rem;
        font-weight: 700;
      }
      @media (max-width: 640px) {
        .name {
          display: none;
        }
      }
      /* cnt-landing's dashboard container: max 80rem, generous padding, on cream. */
      .admin-container {
        max-width: 80rem;
        margin: 0 auto;
        padding: 24px 16px 48px;
      }
      @media (min-width: 768px) {
        .admin-container {
          padding: 32px 32px 64px;
        }
      }
    `,
  ],
})
export class AdminLayoutComponent {
  readonly auth = inject(AuthService);

  /** Two-letter initials for the avatar; "?" when the profile has no name. */
  initials(first?: string, last?: string): string {
    const text = `${first?.trim()?.[0] ?? ''}${last?.trim()?.[0] ?? ''}`.toUpperCase();
    return text || '?';
  }

  logout(): void {
    void this.auth.signOut('You have logged out');
  }
}
